import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
  Badge,
} from "reactstrap";
import SettingsPanel from "../../components/SettingsPanel";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import TableContainer from "../../../../Components/Common/TableContainer"; // Adjust path as needed
import LoadingSpinner from "../../../../Components/Common/Spinner"; // Adjust path as needed
import NoDataContent from "../../../../Components/Common/NoDataContent"; // Adjust path as needed
import HandleDateTime from "../../../../Components/Common/HandleDateTime"; // Adjust path as needed
import DeleteModal from "../../../../Components/Common/DeleteModal"; // Adjust path as needed
import { toast } from "react-toastify";
import { isEmpty } from "lodash";

import {
  getSystemModuleList,
  activateSystemModule,
  deactivateSystemModule,
  deleteSystemModule,
  activateSystemModuleSuccess,
  deactivateSystemModuleSuccess,
  deleteSystemModuleSuccess,
} from "../../../../store/Module/action";
import CreateEditSystemModule from "../../../../Components/popUpComponents/CreateEditSystemModule"; // Adjust path to your actions

export default function AllModulesSettings() {
  const dispatch = useDispatch();
  const history = useHistory();

  // Local States
  const [deleteModal, setDeleteModal] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);

  // Redux Selectors
  const {
    listData,
    listLoading,
    activateSuccess,
    deactivateSuccess,
    deleteSuccess,
  } = useSelector((state) => ({
    listData: state.SystemModule.systemModuleList?.data || [],
    listLoading: state.SystemModule.systemModuleListLoading,
    activateSuccess: state.SystemModule.activateSystemModuleSuccess,
    deactivateSuccess: state.SystemModule.deactivateSystemModuleSuccess,
    deleteSuccess: state.SystemModule.deleteSystemModuleSuccess,
  }));

  // Initial Load
  useEffect(() => {
    dispatch(getSystemModuleList());
  }, [dispatch]);

  // Handle Success Notifications & Refetch
  useEffect(() => {
    if (!isEmpty(activateSuccess)) {
      if (activateSuccess.status === 200) {
        toast.success(
          activateSuccess.message || "Module activated successfully."
        );
        dispatch(getSystemModuleList());
      } else {
        toast.error(activateSuccess.message || "Failed to activate module.");
      }
      dispatch(activateSystemModuleSuccess([])); // Clear success state
    }
  }, [activateSuccess, dispatch]);

  useEffect(() => {
    if (!isEmpty(deactivateSuccess)) {
      if (deactivateSuccess.status === 200) {
        toast.success(
          deactivateSuccess.message || "Module deactivated successfully."
        );
        dispatch(getSystemModuleList());
      } else {
        toast.error(
          deactivateSuccess.message || "Failed to deactivate module."
        );
      }
      dispatch(deactivateSystemModuleSuccess([])); // Clear success state
    }
  }, [deactivateSuccess, dispatch]);

  useEffect(() => {
    if (!isEmpty(deleteSuccess)) {
      if (deleteSuccess.status === 200) {
        toast.success(deleteSuccess.message || "Module deleted successfully.");
        dispatch(getSystemModuleList());
        setDeleteModal(false);
      } else {
        toast.error(deleteSuccess.message || "Failed to delete module.");
      }
      dispatch(deleteSystemModuleSuccess([])); // Clear success state
    }
  }, [deleteSuccess, dispatch]);

  // Keep the login payload's module list (authUser.get_systemmodule_list in
  // localStorage) in sync with any activate/deactivate/create/delete here, so
  // module-gated UI (e.g. the customer Vehicles tab in customerTabSections.js)
  // reacts on the next render instead of waiting for a re-login. Keyed on
  // listData, which is refetched after every mutation.
  useEffect(() => {
    if (isEmpty(listData)) return;
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      if (!authUser) return;
      authUser.get_systemmodule_list = listData.map((module) => ({
        Id: module.Id,
        Name: module.Name,
        Code: module.Code,
        Icon: module.Icon,
        DisplayOrder: module.DisplayOrder,
        IsEnabledByDefault: module.IsEnabledByDefault,
        Active: module.Active,
      }));
      localStorage.setItem("authUser", JSON.stringify(authUser));
    } catch (_) {
      // ignore storage/parse errors
    }
  }, [listData]);

  // Action Handlers
  const handleActivate = (id) => {
    dispatch(activateSystemModule({ id: id }));
  };

  const handleDeactivate = (id) => {
    dispatch(deactivateSystemModule({ id: id }));
  };

  const handleDeleteClick = (id) => {
    setModuleToDelete(id);
    setDeleteModal(true);
  };

  const confirmDelete = () => {
    if (moduleToDelete) {
      // Assuming your API expects an array of IDs for deletion based on the postman body `[4]`
      dispatch(deleteSystemModule([moduleToDelete]));
    }
  };

  const handlePushHistory = () => {
    history.push("/create-module"); // Adjust to your actual create route
  };

  // Add these to your local states in AllModulesSettings.js
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) setSelectedModule(null); // Clear data when closing
  };

  const handleCreateNew = () => {
    setIsEditMode(false);
    setSelectedModule(null);
    toggleModal();
  };

  const handleEditClick = (moduleData) => {
    setIsEditMode(true);
    setSelectedModule(moduleData);
    toggleModal();
  };
  // Table Columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "Id",
        filterable: true,
        Cell: (cellProps) => {
          return <span className="fw-semibold">#{cellProps.value}</span>;
        },
      },
      {
        Header: "Name",
        accessor: "Name",
        filterable: true,
        Cell: (cellProps) => {
          return (
            <div className="d-flex align-items-center">
              {cellProps.row.original.Icon && (
                <i
                  className={`${cellProps.row.original.Icon} me-2 text-primary`}
                ></i>
              )}
              <div className="flex-grow-1 name">{cellProps.value}</div>
            </div>
          );
        },
      },
      {
        Header: "Code",
        accessor: "Code",
        filterable: true,
      },
      {
        Header: "Status",
        accessor: "Active",
        filterable: true,
        Cell: (cellProps) => {
          const isActive = cellProps.value === "1";
          return (
            <Badge color={isActive ? "success" : "danger"} className="p-2">
              {isActive ? "Active" : "Inactive"}
            </Badge>
          );
        },
      },
      {
        Header: "Created Date",
        accessor: "CreatedDateTime",
        filterable: true,
        Cell: (cellProps) => {
          return <div>{HandleDateTime(cellProps.value)}</div>;
        },
      },
      {
        Header: "Action",
        Cell: (cellProps) => {
          const moduleId = cellProps.row.original?.Id;
          const isActive = cellProps.row.original?.Active === "1";

          return (
            <div className="d-flex gap-2">
              {isActive ? (
                <button
                  type="button"
                  className="btn btn-sm btn-soft-warning"
                  onClick={() => handleDeactivate(moduleId)}
                >
                  <i className="ri-close-circle-line align-bottom me-1"></i>
                  Deactivate
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm btn-soft-success"
                  onClick={() => handleActivate(moduleId)}
                >
                  <i className="ri-checkbox-circle-line align-bottom me-1"></i>
                  Activate
                </button>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  document.title = "System Modules | CRMit - Less Work. Do More.";

  return (
    <React.Fragment>
      {/*{isModalOpen && (*/}
      {/*  <CreateEditSystemModule*/}
      {/*    isOpen={isModalOpen}*/}
      {/*    toggle={toggleModal}*/}
      {/*    isEdit={isEditMode}*/}
      {/*    moduleData={selectedModule}*/}
      {/*  />*/}
      {/*)}*/}
      {/* Delete Confirmation Modal */}
      {/*{deleteModal && (*/}
      {/*  <DeleteModal*/}
      {/*    show={deleteModal}*/}
      {/*    onDeleteClick={confirmDelete}*/}
      {/*    onCloseClick={() => setDeleteModal(false)}*/}
      {/*    customText={"Are you sure you want to delete this module?"}*/}
      {/*    customButtonText={"Delete Module"}*/}
      {/*  />*/}
      {/*)}*/}

      <SettingsPanel
        title="All Modules"
        description="System modules available on the platform — activate or deactivate."
      >
        <Row>
          <Col lg={12}>
            <Card id="moduleList">
              <CardHeader className="border-0">
                <Row className="g-4 align-items-center">
                  <Col sm={3}>
                    {/* You can add a local search input here similar to your PurchaseList if desired */}
                  </Col>
                  <div className="col-sm-auto ms-auto">
                    <div className="hstack gap-2">
                      {/*<button*/}
                      {/*  type="button"*/}
                      {/*  className="btn btn-success add-btn"*/}
                      {/*  onClick={handleCreateNew}*/}
                      {/*>*/}
                      {/*  <i className="ri-add-line align-bottom me-1"></i> New*/}
                      {/*  Module*/}
                      {/*</button>*/}
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                <div>
                  {listLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      {!isEmpty(listData) ? (
                        <TableContainer
                          columns={columns}
                          data={listData}
                          isGlobalFilter={true}
                          isAddUserList={false}
                          customPageSize={10}
                          className="custom-header-css"
                          divClass="table-responsive table-card mb-0"
                          tableClass="align-middle table-nowrap"
                          theadClass="text-muted"
                          initialSortBy={[
                            { id: "DisplayOrder", desc: false }, // Sort by DisplayOrder if desired
                          ]}
                        />
                      ) : (
                        <NoDataContent
                          titleText={`No Modules Found`}
                          paragraphText={`Get started by creating your first System Module.`}
                          useButton={true}
                          buttonOnClick={handlePushHistory}
                          buttonText={"Create First Module"}
                        />
                      )}
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </SettingsPanel>
    </React.Fragment>
  );
}
